import { supabase } from '@/lib/supabase/client';
import { leadOutreachService } from '@/lib/services/supabase/lead-outreach';
import { leadsService } from '@/lib/services/supabase/leads';
import { contactVaultService } from '@/lib/services/supabase/contact-vault';
import { dealsService } from '@/lib/services/supabase/deals';
import { leadOperatorsService } from '@/lib/services/supabase/lead-operators';
import {
  emptyWeekQueueEligibilityBreakdown,
  rankLeadsForWeekQueue,
  WEEK_QUEUE_TARGET_SIZE,
  type WeekQueueEligibilityBreakdown,
} from '@/lib/outreach/week-queue-ranking';
import {
  assignWeekQueueOutreach,
  leadNeedsWeekQueueOutreachAssignment,
} from '@/lib/outreach/week-queue-suggestions';
import {
  buildReconcilePlan,
  type ReconcileDisqualifyAction,
  type ReconcilePlan,
} from '@/lib/outreach/reconcile-nurture-prospecting';
import type { LeadList, LeadListMember } from '@/types/leads';
import type { LeadOutreachEvent, OutreachTrack } from '@/types/lead-outreach';
import {
  assertOutreachActivityCommandsEnabled,
  OUTREACH_ACTIVITY_COMMANDS_ENABLED,
} from '@/lib/outreach/activity-release';

export const WEEK_QUEUE_REBUILD_ENABLED = false;
export const WEEK_QUEUE_REBUILD_DISABLED_MESSAGE =
  'Week-queue rebuild is disabled until a separately reviewed delivery release provides a safe ranking path.';

export function getWeekStartMonday(date = new Date()): string {
  const copy = new Date(date);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  copy.setHours(0, 0, 0, 0);
  return copy.toISOString().slice(0, 10);
}

export const leadListsService = {
  async getOrCreateWeekQueue(weekStart = getWeekStartMonday()): Promise<LeadList> {
    const { data, error } = await supabase.rpc('get_or_create_weekly_outreach_list', {
      p_week_start: weekStart,
    });
    if (error) throw new Error(`Failed to find week queue: ${error.message}`);
    const list = Array.isArray(data) ? data[0] : data;
    if (!list) throw new Error('Failed to find week queue: no list returned');
    return list as LeadList;
  },

  async listWeekQueueMembers(weekStart = getWeekStartMonday()): Promise<LeadListMember[]> {
    const { data, error } = await supabase.rpc('list_weekly_outreach_members', {
      p_week_start: weekStart,
    });

    if (error) {
      console.error('Error listing week queue members:', error);
      return [];
    }
    return (data as LeadListMember[]) || [];
  },

  async getWeekQueueLeadIds(weekStart = getWeekStartMonday()): Promise<Set<string>> {
    const members = await this.listWeekQueueMembers(weekStart);
    return new Set(members.map((member) => member.lead_id));
  },

  async isLeadInWeekQueue(leadId: string, weekStart = getWeekStartMonday()): Promise<boolean> {
    const ids = await this.getWeekQueueLeadIds(weekStart);
    return ids.has(leadId);
  },

  async addToWeekQueue(
    leadId: string,
    options?: { track?: OutreachTrack; template_key?: string | null; notes?: string | null }
  ): Promise<void> {
    assertOutreachActivityCommandsEnabled();
    const weekStart = getWeekStartMonday();
    const list = await this.getOrCreateWeekQueue(weekStart);
    const { error } = await supabase.rpc('add_weekly_outreach_member', {
      p_week_start: weekStart,
      p_lead_id: leadId,
      p_added_by: 'manual_outreach',
    });

    if (error) {
      console.error('Error adding lead to week queue:', error);
      throw new Error(`Failed to add to week queue: ${error.message}`);
    }

    let track = options?.track;
    let templateKey = options?.template_key;
    if (!track || !templateKey) {
      const [lead, events] = await Promise.all([
        leadsService.getById(leadId),
        leadOutreachService.listEventsForLeadIds([leadId]),
      ]);
      if (lead) {
        const assignment = assignWeekQueueOutreach(lead, events);
        track = track ?? assignment.track;
        templateKey = templateKey ?? assignment.template_key;
      }
    }

    await leadOutreachService.logQueued({
      lead_id: leadId,
      track: track ?? 'pinch_hit',
      template_key: templateKey ?? null,
      notes: options?.notes ?? `Added to ${list.name}`,
    });
  },

  async addManyToWeekQueue(leadIds: string[]): Promise<{ added: number; alreadyQueued: number }> {
    const weekStart = getWeekStartMonday();
    const existing = await this.getWeekQueueLeadIds(weekStart);
    let added = 0;
    let alreadyQueued = 0;
    for (const leadId of leadIds) {
      if (existing.has(leadId)) {
        alreadyQueued += 1;
        continue;
      }
      await this.addToWeekQueue(leadId);
      existing.add(leadId);
      added += 1;
    }
    return { added, alreadyQueued };
  },

  async removeFromWeekQueue(leadId: string, weekStart = getWeekStartMonday()): Promise<void> {
    assertOutreachActivityCommandsEnabled();
    const { error } = await supabase.rpc('remove_weekly_outreach_members', {
      p_week_start: weekStart,
      p_lead_ids: [leadId],
    });

    if (error) {
      console.error('Error removing lead from week queue:', error);
      throw new Error(`Failed to remove from week queue: ${error.message}`);
    }
  },

  async removeManyFromWeekQueue(leadIds: string[], weekStart = getWeekStartMonday()): Promise<number> {
    if (leadIds.length === 0) return 0;
    assertOutreachActivityCommandsEnabled();

    const { data, error } = await supabase.rpc('remove_weekly_outreach_members', {
      p_week_start: weekStart,
      p_lead_ids: leadIds,
    });

    if (error) {
      console.error('Error removing leads from week queue:', error);
      throw new Error(`Failed to remove from week queue: ${error.message}`);
    }

    return (data as number | null) ?? 0;
  },

  async disqualifyLead(leadId: string, weekStart = getWeekStartMonday()): Promise<void> {
    assertOutreachActivityCommandsEnabled();
    await this.removeFromWeekQueue(leadId, weekStart);

    const { error } = await supabase.rpc('update_leads_redacted', {
      p_lead_ids: [leadId],
      p_changes: { status: 'disqualified', priority_band: 'suppressed' },
    });

    if (error) {
      console.error('Error disqualifying lead:', error);
      throw new Error(`Failed to disqualify lead: ${error.message}`);
    }
  },

  async disqualifyMany(leadIds: string[], weekStart = getWeekStartMonday()): Promise<number> {
    if (leadIds.length === 0) return 0;

    assertOutreachActivityCommandsEnabled();
    await this.removeManyFromWeekQueue(leadIds, weekStart);

    const { error } = await supabase.rpc('update_leads_redacted', {
      p_lead_ids: leadIds,
      p_changes: { status: 'disqualified', priority_band: 'suppressed' },
    });

    if (error) {
      console.error('Error disqualifying leads:', error);
      throw new Error(`Failed to disqualify leads: ${error.message}`);
    }

    return leadIds.length;
  },

  /** Preserve missing-contact leads in the reversible Needs contact lane. */
  async disqualifyNoContactLeads(
    weekStart = getWeekStartMonday(),
    excludeLeadIds: string[] = []
  ): Promise<{ disqualified: number; skippedExcluded: number }> {
    void weekStart;
    const candidates = await leadsService.listNoContactCleanupCandidates(excludeLeadIds);
    const skippedExcluded = excludeLeadIds.length;

    if (candidates.length === 0) {
      return { disqualified: 0, skippedExcluded };
    }

    const { data, error } = await supabase.rpc('mark_leads_needs_contact', {
      p_lead_ids: candidates.map((row) => row.id),
    });
    if (error) throw new Error(`Failed to move leads to needs contact: ${error.message}`);
    return { disqualified: (data as number | null) ?? 0, skippedExcluded };
  },

  async applyReconcileDisqualifyActions(
    _actions: ReconcileDisqualifyAction[],
    _weekStart = getWeekStartMonday()
  ): Promise<number> {
    throw new Error('RECONCILE_MUTATION_DISABLED');
  },

  async previewNurtureProspectingReconcile(): Promise<ReconcilePlan> {
    const [leads, nurtureOwners] = await Promise.all([
      leadsService.listReconcileCandidates(),
      dealsService.listNurtureTrackOwners(),
    ]);
    return buildReconcilePlan(leads, nurtureOwners);
  },

  async reconcileNurtureProspecting(weekStart = getWeekStartMonday()): Promise<{
    plan: ReconcilePlan;
    nurtureDisqualified: number;
    duplicateDisqualified: number;
  }> {
    const plan = await this.previewNurtureProspectingReconcile();
    void weekStart;
    return { plan, nurtureDisqualified: 0, duplicateDisqualified: 0 };
  },

  async clearWeekQueue(weekStart = getWeekStartMonday()): Promise<number> {
    const members = await this.listWeekQueueMembers(weekStart);
    return this.removeManyFromWeekQueue(members.map((member) => member.lead_id), weekStart);
  },

  async rebuildAutoWeekQueue(
    weekStart = getWeekStartMonday(),
    limit = WEEK_QUEUE_TARGET_SIZE
  ): Promise<{
    cleared: number;
    added: number;
    ranked: number;
    eligible: number;
    shortfall: number;
    breakdown: WeekQueueEligibilityBreakdown;
  }> {
    if (!WEEK_QUEUE_REBUILD_ENABLED) {
      throw new Error(WEEK_QUEUE_REBUILD_DISABLED_MESSAGE);
    }
    const candidates = await leadsService.getOutreachRankingCandidates();

    if (candidates.length === 0) {
      return {
        cleared: 0,
        added: 0,
        ranked: 0,
        eligible: 0,
        shortfall: limit,
        breakdown: emptyWeekQueueEligibilityBreakdown(0),
      };
    }

    const [events, vaultMatchesByLeadId, nurtureEmails, ownerOperatorIds] = await Promise.all([
      leadOutreachService.listEventsForLeadIds(candidates.map((lead) => lead.id)),
      contactVaultService.buildVaultMatchesForRankingLeads(candidates),
      dealsService.listNurtureTrackEmails(),
      leadOperatorsService.listOwnerOperatorIds(),
    ]);
    const eventsByLeadId = new Map<string, LeadOutreachEvent[]>();
    for (const event of events) {
      const existing = eventsByLeadId.get(event.lead_id) ?? [];
      existing.push(event);
      eventsByLeadId.set(event.lead_id, existing);
    }

    const { ranked, breakdown, shortfall } = rankLeadsForWeekQueue(
      candidates,
      eventsByLeadId,
      limit,
      vaultMatchesByLeadId,
      nurtureEmails,
      ownerOperatorIds
    );
    if (ranked.length === 0) {
      const replace = await supabase.rpc('replace_weekly_outreach_members', {
        p_week_start: weekStart,
        p_lead_ids: [],
        p_added_by: 'auto_ranked',
      });
      if (replace.error) throw new Error(`Failed to rebuild week queue: ${replace.error.message}`);
      const summary = (replace.data ?? {}) as { previous?: number };
      return { cleared: summary.previous ?? 0, added: 0, ranked: 0, eligible: breakdown.eligible, shortfall, breakdown };
    }

    const list = await this.getOrCreateWeekQueue(weekStart);
    const { data: replaceData, error } = await supabase.rpc('replace_weekly_outreach_members', {
      p_week_start: weekStart,
      p_lead_ids: ranked.map((row) => row.leadId),
      p_added_by: 'auto_ranked',
    });

    if (error) {
      console.error('Error rebuilding auto week queue:', error);
      throw new Error(`Failed to rebuild week queue: ${error.message}`);
    }

    const candidatesById = new Map(candidates.map((lead) => [lead.id, lead]));
    for (const row of ranked) {
      const lead = candidatesById.get(row.leadId);
      if (!lead) continue;
      const leadEvents = eventsByLeadId.get(row.leadId) ?? [];
      const assignment = assignWeekQueueOutreach(lead, leadEvents);
      await leadOutreachService.logQueued({
        lead_id: row.leadId,
        track: assignment.track,
        template_key: assignment.template_key,
        notes: `Auto-queued for ${list.name} · ${assignment.template_key}`,
      });
    }

    return {
      cleared: ((replaceData ?? {}) as { previous?: number }).previous ?? 0,
      added: ranked.length,
      ranked: ranked.length,
      eligible: breakdown.eligible,
      shortfall,
      breakdown,
    };
  },

  /**
   * Persist suggested Track / Template for week-queue members that still lack them
   * (e.g. queues built before auto-assignment existed).
   */
  async ensureWeekQueueOutreachAssignments(
    weekStart = getWeekStartMonday()
  ): Promise<{ filled: number; skipped: number }> {
    if (!OUTREACH_ACTIVITY_COMMANDS_ENABLED) return { filled: 0, skipped: 0 };
    const members = await this.listWeekQueueMembers(weekStart);
    if (members.length === 0) return { filled: 0, skipped: 0 };

    const leadIds = members.map((member) => member.lead_id);
    const [leads, events] = await Promise.all([
      leadsService.getByIds(leadIds),
      leadOutreachService.listEventsForLeadIds(leadIds),
    ]);

    const eventsByLeadId = new Map<string, LeadOutreachEvent[]>();
    for (const event of events) {
      const list = eventsByLeadId.get(event.lead_id) ?? [];
      list.push(event);
      eventsByLeadId.set(event.lead_id, list);
    }

    const list = await this.getOrCreateWeekQueue(weekStart);
    let filled = 0;
    let skipped = 0;

    for (const lead of leads) {
      const leadEvents = eventsByLeadId.get(lead.id) ?? [];
      if (!leadNeedsWeekQueueOutreachAssignment(leadEvents)) {
        skipped += 1;
        continue;
      }
      const assignment = assignWeekQueueOutreach(lead, leadEvents);
      await leadOutreachService.logQueued({
        lead_id: lead.id,
        track: assignment.track,
        template_key: assignment.template_key,
        notes: `Week queue assignment — ${list.name} · ${assignment.template_key}`,
      });
      filled += 1;
    }

    return { filled, skipped };
  },
};
